import { AsyncPipe } from "@angular/common";
import { Component, Input, OnInit, inject } from "@angular/core";
import { UiKitModule } from "@app/ui-kit.module";
import { ConfirmModalComponent } from "@components/confirm-modal/confirm-modal.component";
import { MultiSelectGroupModalComponent } from "@components/multi-select-group-modal/multi-select-group-modal.component";
import { Floorplan } from "@domain/project/floorplan/floorplan";
import { FloorplanSelectionMode } from "@domain/project/floorplan/floorplan-state";
import { nextGroupId } from "@domain/project/group-id.utils";
import { ProjectFloorplanService } from "@domain/project/project-floorplan.service";
import { ModalService } from "@odx/angular/components/modal";
import { FloorplanService } from "@project/floorplanner/floorplan.service";
import { ProjectStateService } from "@project/project-state.service";
import { Observable, map, startWith } from "rxjs";

@Component({
  selector: "app-floorplan-multi-select-bar",
  imports: [UiKitModule, AsyncPipe],
  templateUrl: "./floorplan-multi-select-bar.component.html",
  styleUrl: "./floorplan-multi-select-bar.component.scss",
})
export class FloorplanMultiSelectBarComponent implements OnInit {
  private floorplanService = inject(FloorplanService);
  private projectStateService = inject(ProjectStateService);
  private modalService = inject(ModalService);
  private projectFloorplanService = inject(ProjectFloorplanService);

  @Input({ required: true }) activeFloorplan!: Floorplan;

  protected readonly tooltipGroup = $localize`:@@floorplan.multiSelectBar.group.tooltip:Auswahl gruppieren`;
  protected readonly tooltipGroupDisabled = $localize`:@@floorplan.multiSelectBar.group.tooltip.disabled:Die Elemente können nicht gruppiert werden,<br />da mindestens ein Nicht-Produkt ausgewählt wurde.`;

  protected isMultiSelectMode$!: Observable<boolean>;
  protected selectedCount$!: Observable<number>;

  protected get hasNonGroupableItems(): boolean {
    return this.floorplanService.hasNonGroupableSelectedItems();
  }

  ngOnInit() {
    this.isMultiSelectMode$ = this.projectFloorplanService.floorplanEvents$.pipe(
      startWith(null),
      map(() => this.activeFloorplan.floorplanState.selectionMode === FloorplanSelectionMode.MULTI),
    );

    this.selectedCount$ = this.floorplanService.selectedItems$.pipe(map((items) => items.length));
  }

  protected deselectAll() {
    this.floorplanService.deselectItem();
    this.activeFloorplan.floorplanState.selectionMode = FloorplanSelectionMode.SINGLE;
  }

  protected duplicateAll() {
    this.floorplanService.duplicateSelectedItems();
  }

  protected deleteAll() {
    this.modalService
      .open(ConfirmModalComponent, {
        data: {
          headerText: $localize`:@@floorplan.multiSelectBar.deleteConfirm.header:Elemente löschen?`,
          text: $localize`:@@floorplan.multiSelectBar.deleteConfirm.text:Sollen alle ausgewählten Elemente wirklich gelöscht werden?`,
          confirmButtonText: $localize`:@@global.delete:Löschen`,
        },
      })
      .onClose$.subscribe(() => {
        this.floorplanService.deleteSelectedItems();
      });
  }

  protected groupAll() {
    if (this.floorplanService.hasNonGroupableSelectedItems()) return;
    const hasAlreadyGroupedItems = this.floorplanService.selectedItemsHaveExistingGroup();
    const commonGroup = this.floorplanService.getCommonGroupOfSelectedItems();
    const availableGroups = commonGroup
      ? this.activeFloorplan.project.groups.filter((g) => g.id !== commonGroup.id)
      : this.activeFloorplan.project.groups;
    this.modalService
      .open(MultiSelectGroupModalComponent, {
        data: {
          groups: availableGroups,
          hasAlreadyGroupedItems,
          floorplanId: this.activeFloorplan.id,
          nextGroupId: nextGroupId(this.activeFloorplan.project.groups),
        },
      })
      .onClose$.subscribe((result) => {
        if (!result) return;
        this.floorplanService.addSelectedItemsToGroup(result.group);
      });
  }
}
